import { RPFormattedMessage } from "@src/i18n";
import {
  useOrgInfo,
  CompanyAccountStatus,
  useUserInfo,
  OrgCountry,
} from "@src/common/reliablePartnerApi";
import { useParams } from "react-router-dom";

const CompanyAccountStatusInfo = () => {
  const orgInfo = useOrgInfo();

  if (!orgInfo.isSuccess) {
    return null;
  }

  // We're only interested in informing the user about problems with the company
  // account for now. Also if the status is undefined it means that the user is
  // not authorized to see it.
  if (orgInfo.data?.companyAccountStatus === CompanyAccountStatus.ACTIVE) {
    return null;
  }

  return (
    <div
      className="alert alert-warning mt-5 justify-content-start d-flex"
      role="alert"
    >
      <i
        className="fa fa-info-circle mr-4 align-self-center"
        aria-hidden="true"
      ></i>
      {orgInfo.data?.companyAccountStatus === CompanyAccountStatus.PENDING ? (
        <span>
          <RPFormattedMessage
            id="companyAccountPending"
            defaultMessage="Please finish the registration of your company at <linkToCompanyAccount>company account</linkToCompanyAccount>."
          />
        </span>
      ) : (
        <span>
          <RPFormattedMessage
            id="companyAccountTerminated"
            defaultMessage="Something needs clarifying on your <linkToCompanyAccount>company account</linkToCompanyAccount>. Please contact our <linkToCustomerService>customer service</linkToCustomerService>."
          />
        </span>
      )}
    </div>
  );
};

const PermissionPage = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const userInfo = useUserInfo();
  const orgInfo = useOrgInfo();
  const orgBusinessId = orgInfo.data?.registrationNumber;
  const orgCountry = orgInfo.data?.country || OrgCountry.FI;

  const countryCodeMap = {
    [OrgCountry.EE]: "EST",
    [OrgCountry.FI]: "FIN",
    [OrgCountry.LT]: "LTU",
    [OrgCountry.LV]: "LVA",
    [OrgCountry.PL]: "POL",
    [OrgCountry.SE]: "SWE",
  };

  switch (userInfo.status) {
    case "idle":
    case "loading":
      return <div className="page-loading-indicator" />;
    case "success":
      const company = userInfo.data.companies.find(
        company => company.qvarnId === orgId
      );
      const canAccess =
        userInfo.data.isAdmin || company?.permissions.includes("rp_user");
      return (
        <div className="d-flex flex-row justify-content-center mt-5">
          <div className="col-md-10">
            <h1>
              <RPFormattedMessage
                id="permissionPageHeader"
                defaultMessage="Luotettava Kumppani -palvelun käyttöoikeus"
              />
            </h1>
            {canAccess ? (
              <RPFormattedMessage
                id="permissionPageRPUserPermission"
                defaultMessage="Sinulla on käyttöoikeus Luotettava Kumppani -palveluun yritykselle <b>{orgName}</b>."
              />
            ) : company ? (
              <>
                <p>
                  <RPFormattedMessage
                    id="permissionPageNoPermissionsRepresentedOrg"
                    defaultMessage="Sinulta puuttuu käyttöoikeus Luotettava Kumppani -palveluun yritykselle <b>{orgName}</b>. Pyydä tarpeen vaatiessa käyttöoikeus yrityksenne pääkäyttäjältä. <linkToCompanyAccount>Siirry yritystilille</linkToCompanyAccount>."
                  />
                </p>
                <p>
                  <RPFormattedMessage
                    id="permissionPageReportService"
                    defaultMessage={
                      "Pääset hakemaan yrityksesi sekä muiden Luotettava Kumppani -yritysten raportit <linkToReportService>Yrityshaku-palvelusta</linkToReportService>."
                    }
                    extraRTEProps={{
                      linkToReportService: {
                        countryCode: countryCodeMap[orgCountry],
                        orgBusinessId,
                      },
                    }}
                  />
                </p>
                <p>
                  <RPFormattedMessage
                    id="permissionPageLinkToCompanyAccount"
                    defaultMessage="<linkToCompanyAccount>Siirry yritystilille</linkToCompanyAccount>"
                  />
                </p>
              </>
            ) : (
              <RPFormattedMessage
                id="permissionPageNoPermissionsUnRepresentedOrg"
                defaultMessage="Sinulta puuttuu käyttöoikeus valitsemaasi yritykseen."
              />
            )}
            {(!!company || userInfo.data.isAdmin) && (
              <CompanyAccountStatusInfo />
            )}
          </div>
        </div>
      );
  }
};

export default PermissionPage;
